import React, { useEffect, useState } from 'react'
import { api } from '../lib/api'
import { Link } from 'react-router-dom'
import MCPConnectionDrawer from '../components/MCPConnectionDrawer'

interface MCPServer {
  id: number
  name: string
  slug: string
  description: string
  category: string
  icon_url: string | null
  is_connected: boolean
  user_connection: {
    id: number
    display_name: string
    is_active: boolean
  } | null
}

interface MCPConnection {
  id: number
  display_name: string
  is_active: boolean
  mcp_server: {
    id: number
    name: string
    slug: string
    category: string
  }
  linked_agents: Array<{
    agent_id: number
    agent_name: string
  }>
  created_at: string
}

export default function MCPIntegrations() {
  const [servers, setServers] = useState<MCPServer[]>([])
  const [connections, setConnections] = useState<MCPConnection[]>([])
  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState<'catalogue' | 'connections'>('catalogue')
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [selectedServerId, setSelectedServerId] = useState<number | null>(null)
  const [selectedConnectionId, setSelectedConnectionId] = useState<number | null>(null)

  useEffect(() => {
    loadData()
  }, [])

  const loadData = async () => {
    try {
      setLoading(true)
      const [serversRes, connectionsRes] = await Promise.all([
        api.get('/mcp/servers'),
        api.get('/mcp/connections')
      ])
      setServers(serversRes.data.items || [])
      setConnections(connectionsRes.data.items || [])
    } catch (error) {
      console.error('Error loading MCP data:', error)
    } finally {
      setLoading(false)
    }
  }

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'development': return '🐙'
      case 'productivity': return '📁'
      case 'search': return '🔍'
      case 'booking': return '📅'
      case 'weather': return '🌊'
      default: return '🔌'
    }
  }

  const getCategoryLabel = (category: string) => {
    switch (category) {
      case 'development': return 'Développement'
      case 'productivity': return 'Productivité'
      case 'search': return 'Recherche'
      case 'booking': return 'Réservation'
      case 'weather': return 'Météo'
      default: return 'Autre'
    }
  }

  const handleConnect = (serverId: number) => {
    setSelectedServerId(serverId)
    setSelectedConnectionId(null)
    setDrawerOpen(true)
  }

  const handleEditConnection = (connectionId: number, serverId: number) => {
    setSelectedConnectionId(connectionId)
    setSelectedServerId(serverId)
    setDrawerOpen(true)
  }

  const handleDrawerClose = () => {
    setDrawerOpen(false)
    setSelectedServerId(null)
    setSelectedConnectionId(null)
  }

  const handleConnectionSuccess = () => {
    loadData() // Reload data after successful connection
  }

  const handleDeleteConnection = async (connectionId: number, displayName: string) => {
    if (!confirm(`Êtes-vous sûr de vouloir supprimer la connexion "${displayName}" ?\n\nCette action est irréversible et supprimera également tous les liens avec vos agents.`)) {
      return
    }

    try {
      await api.delete(`/mcp/connections/${connectionId}`)
      loadData() // Reload data after deletion
    } catch (error: any) {
      console.error('Error deleting connection:', error)
      alert(error.response?.data?.detail || 'Erreur lors de la suppression de la connexion')
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 mx-auto mb-4"></div>
          <p className="text-zinc-600 dark:text-zinc-400">Chargement...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="relative overflow-hidden">
      <div aria-hidden className="absolute inset-0 -z-30 bg-gradient-to-b from-emerald-50 via-white to-white dark:from-zinc-950 dark:via-zinc-900 dark:to-zinc-900" />
      <div aria-hidden className="absolute inset-x-0 top-0 -z-40 h-[320px] bg-[radial-gradient(circle_at_top,_rgba(16,185,129,0.25),_transparent_68%)]" />

      <div className="relative mx-auto max-w-[1600px] px-6 py-6">
        <div className="mb-4">
          <button
            onClick={() => window.location.href = '/admin'}
            className="inline-flex items-center gap-2 rounded-full border border-zinc-200/70 dark:border-zinc-800 bg-white/80 dark:bg-zinc-900/70 px-4 py-2 text-sm font-medium text-zinc-700 dark:text-zinc-300 shadow-sm transition hover:bg-zinc-50 dark:hover:bg-zinc-800"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" /></svg>
            Retour
          </button>
        </div>
        <section className="space-y-6 rounded-3xl border border-emerald-100/70 bg-white/90 p-8 shadow-xl shadow-emerald-500/10 dark:border-emerald-900/50 dark:bg-zinc-950/90">
          {/* Description */}
          <p className="text-sm text-zinc-600 dark:text-zinc-400">
            Connectez vos agents à des serveurs MCP pour étendre leurs capacités
          </p>

          {/* Tabs */}
          <div className="flex gap-4 border-b border-zinc-200 dark:border-zinc-800">
        <button
          onClick={() => setActiveTab('catalogue')}
          className={`px-4 py-2 font-medium transition-colors ${
            activeTab === 'catalogue'
              ? 'text-emerald-600 border-b-2 border-emerald-600'
              : 'text-zinc-600 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-zinc-100'
          }`}
        >
          Catalogue ({servers.length})
        </button>
        <button
          onClick={() => setActiveTab('connections')}
          className={`px-4 py-2 font-medium transition-colors ${
            activeTab === 'connections'
              ? 'text-emerald-600 border-b-2 border-emerald-600'
              : 'text-zinc-600 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-zinc-100'
          }`}
        >
          Mes connexions ({connections.length})
        </button>
      </div>

          {/* Catalogue Tab */}
          {activeTab === 'catalogue' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {servers.map((server) => (
            <div
              key={server.id}
              className="bg-white dark:bg-zinc-900 rounded-lg border border-zinc-200 dark:border-zinc-800 p-6 hover:shadow-lg transition-shadow"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <span className="text-3xl">{getCategoryIcon(server.category)}</span>
                  <div>
                    <h3 className="font-semibold text-zinc-900 dark:text-zinc-100">
                      {server.name}
                    </h3>
                    <span className="text-xs text-zinc-500 dark:text-zinc-400">
                      {getCategoryLabel(server.category)}
                    </span>
                  </div>
                </div>
                {server.is_connected && (
                  <span className="px-2 py-1 text-xs font-medium bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300 rounded">
                    Connecté
                  </span>
                )}
              </div>

              <p className="text-sm text-zinc-600 dark:text-zinc-400 mb-4 line-clamp-2 min-h-[40px]">
                {server.description}
              </p>

              <div className="flex gap-2">
                <button
                  onClick={() => handleConnect(server.id)}
                  className={`flex-1 px-4 py-2 text-center text-sm font-medium rounded transition-colors ${
                    server.is_connected
                      ? 'bg-zinc-100 dark:bg-zinc-800 text-zinc-900 dark:text-zinc-100 hover:bg-zinc-200 dark:hover:bg-zinc-700'
                      : 'bg-emerald-600 text-white hover:bg-emerald-700'
                  }`}
                >
                  {server.is_connected ? '➕ Ajouter connexion' : 'Connecter'}
                </button>
                {server.is_connected && (
                  <button
                    onClick={() => setActiveTab('connections')}
                    className="px-4 py-2 text-center text-sm font-medium bg-zinc-100 dark:bg-zinc-800 text-zinc-900 dark:text-zinc-100 rounded hover:bg-zinc-200 dark:hover:bg-zinc-700 transition-colors"
                    title="Voir mes connexions"
                  >
                    📋
                  </button>
                )}
              </div>
            </div>
          ))}
          </div>
          )}

          {/* Connections Tab */}
          {activeTab === 'connections' && (
        <div className="space-y-4">
          {connections.length === 0 ? (
            <div className="text-center py-12 bg-white dark:bg-zinc-900 rounded-lg border border-zinc-200 dark:border-zinc-800">
              <span className="text-4xl mb-4 block">🔌</span>
              <p className="text-zinc-600 dark:text-zinc-400 mb-4">
                Aucune connexion MCP configurée
              </p>
              <button
                onClick={() => setActiveTab('catalogue')}
                className="px-4 py-2 bg-emerald-600 text-white rounded hover:bg-emerald-700 transition-colors"
              >
                Parcourir le catalogue
              </button>
            </div>
          ) : (
            connections.map((conn) => (
              <div
                key={conn.id}
                className="bg-white dark:bg-zinc-900 rounded-lg border border-zinc-200 dark:border-zinc-800 p-6"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">{getCategoryIcon(conn.mcp_server.category)}</span>
                    <div>
                      <h3 className="font-semibold text-zinc-900 dark:text-zinc-100">
                        {conn.display_name}
                      </h3>
                      <p className="text-sm text-zinc-500 dark:text-zinc-400">
                        {conn.mcp_server.name}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {conn.is_active ? (
                      <span className="px-2 py-1 text-xs font-medium bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300 rounded">
                        Actif
                      </span>
                    ) : (
                      <span className="px-2 py-1 text-xs font-medium bg-zinc-100 text-zinc-700 dark:bg-zinc-800 dark:text-zinc-300 rounded">
                        Inactif
                      </span>
                    )}
                  </div>
                </div>

                {conn.linked_agents.length > 0 && (
                  <div className="mb-4">
                    <p className="text-sm text-zinc-600 dark:text-zinc-400 mb-2">
                      Agents connectés:
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {conn.linked_agents.map((agent) => (
                        <Link
                          key={agent.agent_id}
                          to={`/agents/${agent.agent_id}`}
                          className="px-2 py-1 text-xs bg-zinc-100 dark:bg-zinc-800 text-zinc-700 dark:text-zinc-300 rounded hover:bg-zinc-200 dark:hover:bg-zinc-700 transition-colors"
                        >
                          {agent.agent_name}
                        </Link>
                      ))}
                    </div>
                  </div>
                )}

                <div className="flex gap-2">
                  <button
                    onClick={() => handleEditConnection(conn.id, conn.mcp_server.id)}
                    className="flex-1 px-4 py-2 text-sm font-medium bg-zinc-100 dark:bg-zinc-800 text-zinc-900 dark:text-zinc-100 rounded hover:bg-zinc-200 dark:hover:bg-zinc-700 transition-colors"
                  >
                    ⚙️ Configurer
                  </button>
                  <button
                    onClick={() => handleDeleteConnection(conn.id, conn.display_name)}
                    className="px-4 py-2 text-sm font-medium bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 rounded hover:bg-red-200 dark:hover:bg-red-900/50 transition-colors"
                    title="Supprimer cette connexion"
                  >
                    🗑️
                  </button>
                </div>
              </div>
            ))
          )}
          </div>
          )}
        </section>
      </div>

      {/* Connection Drawer */}
      <MCPConnectionDrawer
        open={drawerOpen}
        onClose={handleDrawerClose}
        serverId={selectedServerId}
        connectionId={selectedConnectionId}
        onSuccess={handleConnectionSuccess}
      />
    </div>
  )
}
